import { useRouter, useRouterState } from "@tanstack/react-router";
import { Breadcrumbs } from "./Blocks";

/** Human labels for known first-level path segments. */
const SEGMENT_LABELS: Record<string, string> = {
  "about": "About",
  "airbnb-short-term-rental-loans": "Airbnb / STR Loans",
  "apply": "Apply",
  "bridge-loans": "Bridge Loans",
  "brrrr-calculator": "BRRRR Calculator",
  "rental-property-deal-analyzer": "Rental Property Deal Analyzer",
  "business-bank-account": "Business Bank Account",
  "business-loans": "Business Loans",
  "business-setup": "Business Setup",
  "compare": "Compare",
  "compare-cities": "Compare Cities",
  "construction-loans": "Construction Loans",
  "contact": "Contact",
  "difficult-development-areas": "Difficult Development Areas",
  "dscr-calculator": "DSCR Calculator",
  "dscr-loans": "DSCR Loans",
  "duns-number": "DUNS Number",
  "faq": "FAQ",
  "fix-and-flip-loans": "Fix & Flip Loans",
  "foreign-national-loans": "Foreign National Loans",
  "glossary": "Glossary",
  "hard-money-loans": "Hard Money Loans",
  "how-dscr-works": "How DSCR Works",
  "hud-fair-market-rents": "HUD Fair Market Rents",
  "hud-income-limits": "HUD Income Limits",
  "investor-journey": "Investor Journey",
  "itin": "ITIN",
  "landlord-insurance": "Landlord Insurance",
  "legal": "Legal",
  "licensing": "Licensing",
  "llc": "LLC Formation",
  "llc-journey": "Buying in an LLC",
  "multifamily-loans": "Multifamily Loans",
  "partners": "Partners",
  "portfolio-rental-loans": "Portfolio Rental Loans",
  "privacy": "Privacy Policy",
  "process": "Process",
  "property-management-software": "Property Management Software",
  "qualified-census-tracts": "Qualified Census Tracts",
  "rates": "Rates",
  "section-8-investing-map": "Section 8 Investing Map",
  "section-8-loans": "Section 8 DSCR Loans",
  "terms": "Terms of Use",
  "thank-you": "Thank You",
  "thanks": "Thank You",
  "title-services": "Title Services",
  "tools": "Tools",
  "where-we-lend": "Where We Lend",
};

const SMALL_WORDS = new Set(["of", "and", "the", "in", "on", "for", "at", "to", "by"]);

function titleize(slug: string) {
  return slug
    .split("-")
    .map((word, i) => {
      if (i > 0 && SMALL_WORDS.has(word)) return word;
      if (/^\d/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function labelFor(segment: string) {
  return SEGMENT_LABELS[segment] ?? titleize(decodeURIComponent(segment));
}

/**
 * Derives a breadcrumb trail from the current URL so every page has one
 * without each route repeating the markup. Pages that render their own
 * <Breadcrumbs /> opt out via SiteShell's hideBreadcrumbs prop.
 */
export function AutoBreadcrumbs() {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  /**
   * A parent segment only gets a link when a real route answers that path
   * (e.g. `/tools` has no page of its own, only `/tools/compare-cities`), so
   * breadcrumbs never point at a 404.
   */
  const hasRoute = (path: string) => {
    try {
      return router.matchRoutes(path, undefined).some((m) => {
        const id = String(m.routeId);
        return id !== "__root__" && !id.endsWith("/$") && !id.includes("404");
      });
    } catch {
      return false;
    }
  };

  const items: { name: string; href?: string }[] = [{ name: "Home", href: "/" }];
  let acc = "";
  segments.forEach((seg, i) => {
    acc += `/${seg}`;
    const isLast = i === segments.length - 1;
    items.push({ name: labelFor(seg), href: isLast || !hasRoute(acc) ? undefined : acc });
  });

  return <Breadcrumbs items={items} />;
}
